import { Injectable, inject } from '@angular/core';
import { Observable, catchError, throwError, map, forkJoin, of, switchMap, take, ReplaySubject, tap, shareReplay } from 'rxjs';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Event, Partner, User, Station, ApiResponse } from '@app/shared/_models/common';
import { RecupererParamPS, ParamPS } from 'mercalink-fselib';
import * as constants from '@app/shared/constants';

import * as fselib from 'mercalink-fselib';

import { LoginService } from '@app/common/login.service';
import { environment } from '../../environments/environment';
import { handleHttpError } from '@app/shared/util';
import Swal from 'sweetalert2';

export interface BankLetteringLot {
  paymentId: number;
  rspId: number | null;
  orderId: number;
  fseNumber: string;
  partType: 'AMO' | 'AMC' | 'PATIENT';
  payerId: number;
  payerName: string;
  organismCode: string;
  dateComptable: string | null;
  numSecu: string | null;
  patientName: string;
  libVirement: string;
  libVirement2?: string;
  amount: number;
  amountExpected: number;
  receivableId: number;
}

export interface BankLetteringLotDetail {
  paymentId: number;
  paymentAmount: number; // Decimal (float format)
  paymentDate: string; // ISO String format
  orderId: number;
  fseNumber: number;
  receivableId: number;
  amountExpected: number; // Decimal (float format)
  partnerId: number;
  partnerName: string;
  patientSecu: string | null;
  partType: 'AMO' | 'AMC' | 'PATIENT';
}

@Injectable({ providedIn: 'root' })
export class FseService {
  private userSubject = new ReplaySubject<User>(1);
  public user$: Observable<User> = this.userSubject.asObservable();
  private userInitialized = false;

  public loginService: LoginService = inject(LoginService);
  private http: HttpClient = inject(HttpClient);
  private meklibService: fselib.MercalinkFselibService = inject(fselib.MercalinkFselibService);

  private initializeUser(): Observable<User> {
    // Initialize user when needed
    if (this.userInitialized) {
      return this.user$;
    }

    if (!this.loginService.loggeduser?.id) {
      return of(null as any);
    }

    return this.getUser$(this.loginService.loggeduser.id).pipe(
      tap((user: User) => {
        this.userSubject.next(user);
        this.userInitialized = true;
      }),
      shareReplay(1),
    );
  }

  public getUserWhenReady$(): Observable<User> {
    // Method to ensure user is available
    if (this.userInitialized) {
      return this.user$;
    }
    return this.initializeUser();
  }

  /**
   * Retrieves the numIdentFact for the logged-in user.
   * Ensures the user data is loaded before attempting to extract numIdentFact.
   * Emits undefined if numIdentFact is not found or an error occurs.
   */
  public getNumIdentFact$(): Observable<string | undefined> {
    return this.getUserWhenReady$().pipe(
      map(user => user?.data?.FSE?.PS?.numIdentFact),
      tap(numIdentFact => {
        if (!numIdentFact) {
          console.error('numIdentFact is missing from user data. User configuration error.');
        }
      }),
      catchError(err => {
        console.error('Error retrieving numIdentFact:', err);
        return of(undefined);
      }),
      shareReplay(1), // Ensure multiple subscribers don't trigger multiple fetches
    );
  }

  getFses$(q: string): Observable<fselib.FSEOrder[]> {
    return this.http.get<fselib.FSEOrder[]>(`${environment.server_url}/hc/fses?${q}`);
  }

  getFse$(id_order: number): Observable<fselib.FSEOrder[]> {
    return this.http.get<any>(`${environment.server_url}/hc/fse/${id_order}`);
  }

  getRsps$(): Observable<fselib.FseRsp[]> {
    return this.http.get<fselib.FseRsp[]>(`${environment.server_url}/hc/rsps`);
  }

  getRspsOpPonct$(): Observable<fselib.FseRsp[]> {
    return this.http.get<fselib.FseRsp[]>(`${environment.server_url}/hc/rspsponct`);
  }

  getRspsVirement$(q: string): Observable<Virement[]> {
    return this.http.get<Virement[]>(`${environment.server_url}/hc/rspsvirement`).pipe(
      map((virements: Virement[]) => {
        virements.forEach(v => {
          v.isPonctuel = v.rsps.some(rsp => rsp.typeRetour === '03');
        });
        return virements;
      }),
    );
  }

  getBankLetteringLots$(): Observable<BankLetteringLot[]> {
    return this.http.get<ApiResponse<BankLetteringLot[]>>(`${environment.server_url}/hc/bank-lettering/lots`).pipe(
      map(res => {
        if (!res || !res.success) {
          throw new Error(res?.error_msg || res?.msg || 'Erreur lors du chargement des règlements');
        }
        return res.data || [];
      }),
      catchError(err => handleHttpError(err, 'Erreur lors du chargement des règlements')),
    );
  }

  getBankLetteringErrors$(): Observable<fselib.FseRsp[]> {
    return this.http.get<ApiResponse<fselib.FseRsp[]>>(`${environment.server_url}/hc/bank-lettering/lettering-errors`).pipe(
      map(res => {
        if (!res || !res.success) {
          throw new Error(res?.error_msg || res?.msg || 'Erreur lors du chargement des anomalies RSP');
        }
        return res.data || [];
      }),
      catchError(err => handleHttpError(err, 'Erreur lors du chargement des anomalies RSP')),
    );
  }

  getBankLetteringLotDetails$(paymentId: number): Observable<BankLetteringLotDetail[]> {
    return this.http.get<ApiResponse<BankLetteringLotDetail[]>>(`${environment.server_url}/hc/bank-lettering/lots/${paymentId}/details`).pipe(
      map(res => {
        if (!res || !res.success) {
          throw new Error(res?.error_msg || res?.msg || 'Erreur lors du chargement des détails');
        }
        return res.data || [];
      }),
      catchError(err => handleHttpError(err, 'Erreur lors du chargement des détails')),
    );
  }

  reconcileBankLettering$(paymentId: number, userId: number, stationId: number): Observable<any> {
    return this.http
      .post<ApiResponse<any>>(`${environment.server_url}/hc/bank-lettering/lots/${paymentId}/reconcile`, {
        userId,
        stationId,
      })
      .pipe(
        map(res => {
          if (!res || !res.success) {
            throw new Error(res?.error_msg || res?.msg || 'Erreur lors du rapprochement bancaire');
          }
          return res.data;
        }),
        catchError(err => handleHttpError(err, 'Erreur lors du rapprochement bancaire')),
      );
  }

  reconcileBankLetteringBatch$(payload: { paymentIds: number[]; userId: number; stationId: number; bankDate?: string; bankReference?: string; note?: string }): Observable<any> {
    return this.http.post<ApiResponse<any>>(`${environment.server_url}/hc/bank-lettering/reconcile-batch`, payload).pipe(
      map(res => {
        if (!res || !res.success) {
          throw new Error(res?.error_msg || res?.msg || 'Erreur lors du rapprochement bancaire');
        }
        return res.data;
      }),
      catchError(err => handleHttpError(err, 'Erreur lors du rapprochement bancaire')),
    );
  }

  getUser$(id: number): Observable<User> {
    return this.http.get<User[]>(`${environment.server_url}/user/${id}`).pipe(
      take(1),
      map(users => {
        if (!users || users.length === 0) {
          throw new Error('No user found');
        }
        return users[0];
      }),
    );
  }

  getLastLotExtraction$(): Observable<string> {
    return this.http.get<string>(`${environment.server_url}/hc/last_lot_extraction`).pipe(
      catchError((error: any) => {
        console.error('[getLastLotExtraction$] Failed to fetch last lot extraction date:', error);
        return throwError(() => new Error('Erreur au niveau de getLastLotExtraction$'));
      }),
    );
  }

  getLastRspsExtraction$(): Observable<string> {
    return this.http.get<string>(`${environment.server_url}/hc/last_rsps_extraction`).pipe(
      catchError((error: any) => {
        console.error('[getLastRspsExtraction$] Failed to fetch last lot extraction date:', error);
        return throwError(() => new Error('Erreur au niveau de getLastRspsExtraction$'));
      }),
    );
  }

  putLastExtractionDate$(key: string): Observable<void> {
    // Update last lot extraction date
    return this.http.put<void>(`${environment.server_url}/hc/last_extraction_date`, { key }).pipe(
      catchError((error: any) => {
        console.error('[putLastExtractionDate$] Failed to update last lot extraction date:', error);
        return throwError(() => new Error('Erreur au niveau de putLastExtractionDate$'));
      }),
    );
  }

  putAnnulDRE$(id_order: string | number): Observable<void> {
    // Update last lot extraction date
    return this.http.put<void>(`${environment.server_url}/hc/annul_dre/${id_order}`, {}).pipe(
      catchError((error: any) => {
        console.error('[putLastLotExtraction$] Failed to update last lot extraction date:', error);
        return throwError(() => new Error('Erreur au niveau de putLastLotExtraction$'));
      }),
    );
  }

  // Post card data (card inserted)
  postFseCardEvent$(server_url: string, eventtyp: number, userid: number, data: any): Observable<any> {
    let event: Event = {
      id: 0,
      typ: eventtyp, // 1: Insert, 2: Update
      userid: userid,
      objectid: 0,
      objecttyp: constants.ObjectType.HCCARD,
      data: data,
    };
    return this.http.post<any>(server_url + '/event', event, { headers: constants.jsonheader });
  }

  // Post patient update (set partner)
  postFseUpdatePatientEvent$(server_url: string, eventtyp: number, userid: number, objectid: number, data: any): Observable<any> {
    let event: Event = {
      id: 0,
      typ: eventtyp, // 1: Insert, 2: Update
      userid: userid,
      objectid: objectid,
      objecttyp: constants.ObjectType.HCCARD,
      data: data,
    };
    return this.http.post<any>(server_url + '/event', event, { headers: constants.jsonheader });
  }

  // Post batch insert event
  postFseBatchEvent$(server_url: string, eventtyp: number, data: any): Observable<any> {
    let event: Event = {
      id: 0,
      typ: eventtyp, // 1: Insert, 2: Update
      userid: this.loginService.loggeduser?.id,
      objectid: 0,
      objecttyp: constants.ObjectType.HCBATCH,
      data: data,
    };
    return this.http.post<any>(server_url + '/event', event, { headers: constants.jsonheader });
  }

  // Post RSP insert event
  postFseRSPEvent$(server_url: string, data: any): Observable<any> {
    let event: Event = {
      id: 0,
      typ: 1, // 1: Insert
      userid: this.loginService.loggeduser?.id,
      objectid: 0,
      objecttyp: constants.ObjectType.HCRSP,
      data: data,
    };
    return this.http.post<any>(server_url + '/event', event, { headers: constants.jsonheader });
  }

  // Post RSP Lettering event (to trigger process_rsps_into_draft_payments on the server)
  postFseRspLetteringEvent$(server_url: string): Observable<any> {
    let event: Event = {
      id: 0,
      typ: constants.EventType.FSE_RSP_LETTERING,
      userid: this.loginService.loggeduser?.id,
      objectid: 0,
      objecttyp: constants.ObjectType.HCRSP,
      data: {},
    };
    return this.http.post<any>(server_url + '/event', event, { headers: constants.jsonheader });
  }

  // Post RSP EVENTTYP_FSE_UPDATERSPNUMFACTURE event. Update fse_rsp.num_facture
  postFseRSPUpdateNumFactureEvent$(server_url: string, objectid: number, data: any): Observable<any> {
    let event: Event = {
      id: 0,
      typ: constants.EventType.FSE_UPDATE_RSP_NUM_FACTURE,
      userid: this.loginService.loggeduser?.id,
      objectid: objectid,
      objecttyp: constants.ObjectType.HCRSP,
      data: data,
    };
    return this.http.post<any>(server_url + '/event', event, { headers: constants.jsonheader });
  }

  // Post doc update event
  public postDocEvent$(server_url: string, data: any): Observable<any> {
    let event: any = {
      id: 0,
      typ: constants.EventType.UPDATE, // 1: Insert, 2: Update
      userid: 0,
      objectid: 0,
      objecttyp: constants.ObjectType.HCDOC,
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, { headers: constants.jsonheader });
  }

  public postDocPrintEvent$(server_url: string, data: any): Observable<any> {
    // EVENTTYP_PRINT => Print ordo bordereau
    let event: any = {
      id: 0,
      typ: constants.EventType.PRINT,
      userid: 0,
      objectid: 0,
      objecttyp: constants.ObjectType.HCDOC,
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, { headers: constants.jsonheader });
  }

  public postDuplicataPrintEvent$(server_url: string, id: number): Observable<any> {
    // EVENTTYP_PRINT => Print duplicata ordo
    let event: any = {
      id: 0,
      typ: constants.EventType.PRINT,
      userid: 0,
      objectid: id,
      objecttyp: constants.ObjectType.HCORDER,
      data: {},
    };
    return this.http.post<any>(`${server_url}/event`, event, { headers: constants.jsonheader });
  }

  public postQuittancePrintEvent$(server_url: string, id: number): Observable<any> {
    // FSE_PRINT_QUITTANCE => Print quittance FSE
    let event: any = {
      id: 0,
      typ: constants.EventType.FSE_PRINT_QUITTANCE,
      userid: 0,
      objectid: id,
      objecttyp: constants.ObjectType.HCORDER,
      data: {},
    };
    return this.http.post<any>(`${server_url}/event`, event, { headers: constants.jsonheader });
  }

  public postFSEDeleteEvent$(server_url: string, data: any): Observable<any> {
    let event: any = {
      id: 0,
      typ: constants.EventType.DELETE,
      userid: 0,
      objectid: 0,
      objecttyp: constants.ObjectType.HCORDER,
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, { headers: constants.jsonheader });
  }

  public postFSEUpdateStateEvent$(server_url: string, data: any): Observable<any> {
    // Update fse_doc.etat_facture
    let event: any = {
      id: 0,
      typ: constants.EventType.FSE_UPDATE_ORDER_STATE,
      userid: 0,
      objectid: 0,
      objecttyp: constants.ObjectType.HCORDER,
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, { headers: constants.jsonheader });
  }

  // Call AF function 'mettreEnLot' then call updateFses (ExtractLots then update fse_order state)
  public mettreEnLot$(area_baseurl: string, configarea: fselib.ConfigArea): Observable<void> {
    return this.getUserWhenReady$().pipe(
      switchMap((user: User) => {
        const numIdentFact = user?.data?.FSE?.PS?.numIdentFact;
        const numSituation = user?.data?.FSE?.PS?.numSituation;
        if (!numIdentFact || !numSituation) {
          return throwError(() => new Error('Profil FSE non configuré pour cet utilisateur (numIdentFact/numSituation manquant)'));
        }
        return this.meklibService.mettreEnLot$(area_baseurl, numIdentFact, numSituation, configarea).pipe(
          map(response => ({ response, user })), // Pass user along with response
        );
      }),
      switchMap(({ response, user }) => {
        // Handle the case where mettreEnLotResponse is undefined
        if (!response) {
          console.error('mettreEnLot returned undefined');
          return throwError(() => new Error('mettreEnLot returned undefined'));
        }

        if (response.HTTPResponse.codeRetour !== '0') {
          // Handle non-success response from mettreEnLot
          console.error(`mettreEnLot failed with codeRetour: ${response.HTTPResponse.codeRetour}`);
          return throwError(() => new Error(`Non-success response from mettreEnLot: ${response.HTTPResponse.codeRetour}`));
        }

        return this.extractLots$(area_baseurl, user.data.FSE.PS.numIdentFact);
      }),
      catchError((error: any) => {
        // Handle errors in the chain
        console.error('An error occurred during the chaining process:', error);
        return throwError(() => new Error(error.message || 'Erreur à la mise en lot'));
      }),
      map(() => {
        // Transform the observable to emit a void value, signaling completion
        return;
      }),
    );
  }

  public mettreEnFichier$(area_baseurl: string, zoneMsg: string): Observable<void> {
    const numAgrement = environment.production ? 'RZBW1G0O0100' : 'TESTCNDA1418'; // MUST be hard coded. Cf Agrément SESAM (RZBW1G0O0100 in prod, TESTCNDA1418 in dev/test)

    zoneMsg = zoneMsg.replace(/[^a-zA-Z0-9 ]/g, ' ');

    return this.getUserWhenReady$().pipe(
      switchMap((user: User) => {
        const numIdentFact = user?.data?.FSE?.PS?.numIdentFact;
        const numSituation = user?.data?.FSE?.PS?.numSituation;
        if (!numIdentFact || !numSituation) {
          console.error('Erreur. Impossible de lire les données utilisateur : user?.data?.FSE?.PS?.numSituation');
          return throwError(() => new Error('Erreur : profil FSE non configuré (numIdentFact/numSituation manquant)'));
        }

        return this.meklibService.mettreEnFichier$(area_baseurl, numIdentFact, numAgrement, numSituation, zoneMsg).pipe(map(response => ({ response, user })));
      }),
      switchMap(({ response, user }) => {
        if (!response) {
          return throwError(() => new Error('mettreEnFichier returned undefined'));
        }

        if (response.HTTPResponse.codeRetour !== '0') {
          return throwError(() => new Error(`Non-success response from mettreEnFichier: ${response.HTTPResponse.codeRetour}`));
        }

        return this.extractLots$(area_baseurl, user.data.FSE.PS.numIdentFact);
      }),
      catchError((error: any) => {
        console.error('An error occurred during the chaining process:', error);
        return throwError(() => new Error(error.message || 'Erreur à la mise en fichier'));
      }),
      map(() => {
        return;
      }),
    );
  }

  public teletransmettre$(area_baseurl: string): Observable<void> {
    return this.getUserWhenReady$().pipe(
      switchMap((user: User) => {
        const numIdentFact = user?.data?.FSE?.PS?.numIdentFact;
        if (!numIdentFact) {
          return throwError(() => new Error('Profil FSE non configuré pour cet utilisateur (numIdentFact manquant)'));
        }

        return this.meklibService.teletransmettre$(area_baseurl, numIdentFact).pipe(map(response => ({ response, numIdentFact })));
      }),
      switchMap(({ response, numIdentFact }) => {
        if (!response) {
          return throwError(() => new Error('Erreur à la teletransmission, pas de réponse'));
        }

        if (response.HTTPResponse.codeRetour !== '0') {
          return throwError(() => new Error(`Erreur à la teletransmission: ${response.HTTPResponse.codeRetour} - ${response.HTTPResponse.libRetour}`));
        }

        return this.extractLots$(area_baseurl, numIdentFact);
      }),
      catchError((error: any) => {
        console.error('An error occurred during the teletransmettre$ chaining process:', error);
        return throwError(() => new Error('Erreur à la teletransmission'));
      }),
      map(() => {
        return;
      }),
    );
  }

  // Run ExtractLots on AF then send each lot (should only be a few) to mek server for fse_order updating
  extractLots$(area_baseurl: string, numIdentFact: string): Observable<fselib.ExtractLots> {
    // Update FSEs with last extraction date
    return this.getLastLotExtraction$().pipe(
      // Fetch last extraction date from DB
      switchMap((lastExtractionDate_str: string) => {
        // ISO format
        const lastLotExtraction = new Date(lastExtractionDate_str); // Subtract 10 seconds from lastExtractionDate
        console.log(`\x1b[35mlastLotExtraction: ${lastLotExtraction}\x1b[0m`);
        lastLotExtraction.setSeconds(lastLotExtraction.getSeconds() - 10);
        //const adjustedDate = lastLotExtraction.toISOString();    // Convert back to ISO format
        const formattedDate = DateHelper.convertDateToFseLocalFormat(lastLotExtraction);

        console.log(`\x1b[35mformattedDate: ${formattedDate}\x1b[0m`);

        // formattedDate = DateHelper.convertIsoToFseFormat(new Date('2020-01-01 18:00:00.000').toISOString());	// FIXME TESTS RELOAD ALL

        return this.meklibService.extraireLots$(area_baseurl, numIdentFact, formattedDate).pipe(
          // Fetch lots from Area using the formatted date
          switchMap((extractLots: fselib.ExtractLots) => {
            if (!extractLots || !extractLots.lot) {
              return new Observable<fselib.ExtractLots>(observer => {
                observer.next(extractLots || { dateHeureGeneration: '', lot: [] }); // Return an empty structure if undefined
                observer.complete();
              });
            }

            // Call postFseBatchEvent$ once with the entire extractLots object
            return this.postFseBatchEvent$(environment.server_url, constants.EventType.INSERT, extractLots).pipe(
              switchMap(() => {
                return this.putLastExtractionDate$('lots').pipe(
                  // Update the last extraction date with current date
                  map(() => extractLots), // Return the original extractLots after the update
                  catchError((error: any) => {
                    console.error('[updateFses] Error updating last extraction date:', error);
                    return throwError(() => new Error('Error updating last extraction date'));
                  }),
                );
              }),
              catchError((error: any) => {
                console.error('[updateFses] Error during postFseBatchEvent$ call:', error);
                Swal.fire({ icon: 'error', title: 'Erreur serveur FSE', text: String(error) });
                return throwError(() => new Error('Error during postFseBatchEvent$ call'));
              }),
            );
          }),
          catchError((error: any) => {
            console.error('[updateFses] An error occurred during the extraireLots process:', error);
            return throwError(() => new Error('Erreur au niveau de extraireLots'));
          }),
        );
      }),
    );
  }

  // Run ExtractRSPs on AF then send each lot (should only be a few) to mek server for fse_order updating
  extractRSPs$(area_baseurl: string, numIdentFact: string): Observable<fselib.ExtractRSPs> {
    // Update FSEs with last extraction date
    return this.getLastRspsExtraction$().pipe(
      // Fetch last extraction date from DB
      switchMap((lastExtractionDate_str: string) => {
        // ISO format
        const lastLotExtraction = new Date(lastExtractionDate_str); // Subtract 10 seconds from lastExtractionDate
        console.log(`\x1b[35mlastLotExtraction: ${lastLotExtraction}\x1b[0m`);
        lastLotExtraction.setSeconds(lastLotExtraction.getSeconds() - 10);
        //const adjustedDate = lastLotExtraction.toISOString();    // Convert back to ISO format
        const formattedDate = DateHelper.convertDateToFseLocalFormat(lastLotExtraction);

        return this.meklibService.extraireRSPs$(area_baseurl, numIdentFact, formattedDate).pipe(
          // Fetch RSPs from Area using the formatted date
          switchMap((extractRSPs: fselib.ExtractRSPs) => {
            if (!extractRSPs?.fichierRSP?.length) {
              return of({ dateHeureGeneration: '', fichierRSP: [] });
            }

            // Create an array of observables for all postFseBatchEvent$ calls
            const postRSPsObservables = extractRSPs.fichierRSP.map((rsp: fselib.FichierRSP) => {
              return this.postFseRSPEvent$(environment.server_url, rsp).pipe(
                // Call Insert RSP api endpoint
                catchError((error: any) => {
                  console.error('Error posting lot:', error);
                  Swal.fire({ icon: 'error', title: 'Erreur serveur FSE', text: String(error) });
                  return throwError(() => new Error('Error posting rsp'));
                }),
              );
            });

            return forkJoin(postRSPsObservables).pipe(
              // Wait for all postFseBatchEvent$ calls to complete, then update the last extraction date
              switchMap(() => {
                return this.putLastExtractionDate$('rsps').pipe(
                  map(() => extractRSPs),
                  catchError((error: any) => {
                    console.error('[extractRSPs$] Error updating last extraction date for RSPs:', error);
                    return throwError(() => new Error('Error updating last extraction date for RSPs'));
                  }),
                );
              }),
              catchError((error: any) => {
                console.error('[extractRSPs$] Error during postFseRSPEvent$ calls:', error);
                return throwError(() => new Error('Error during postFseRSPEvent$ calls'));
              }),
            );
          }),
          catchError((error: any) => {
            console.error('[updateFses] An error occurred during the extraireLots process:', error);
            return throwError(() => new Error('Erreur au niveau de extractRSPs'));
          }),
        );
      }),
    );
  }

  // Run ExtractScor on AF then send each lot (should only be a few) to mek server for fse_order updating
  extractSCOR$(area_baseurl: string, numIdentFact: string): Observable<fselib.ExtractSCOR> {
    // Update fse_doc with last extraction date
    //return this.getLastLotExtraction$().pipe( // Fetch last extraction date from DB
    return of('2020-01-01 18:00:00.000').pipe(
      // FIXME  Fetch last extraction date from DB
      switchMap((lastExtractionDate_str: string) => {
        // ISO format
        const lastExtractionDate = new Date(lastExtractionDate_str); // Subtract 10 seconds from lastExtractionDate
        lastExtractionDate.setSeconds(lastExtractionDate.getSeconds() - 10);

        const formattedDate = DateHelper.convertDateToFseLocalFormat(lastExtractionDate);

        return this.meklibService.extraireSCOR$(area_baseurl, numIdentFact, formattedDate).pipe(
          // Fetch lots using the formatted date
          switchMap((extractSCORE: fselib.ExtractSCOR) => {
            if (!extractSCORE || !extractSCORE.dossier) {
              return new Observable<fselib.ExtractSCOR>(observer => {
                observer.next(extractSCORE || { dateHeureGeneration: '', dossier: [] }); // Return an empty structure if undefined
                observer.complete();
              });
            }

            if (!Array.isArray(extractSCORE.dossier)) {
              const f = extractSCORE.dossier;
              extractSCORE.dossier = [];
              extractSCORE.dossier.push(f); // Wrap dossier in an array if it's not already one
            }

            // Create an array of observables for all postDocEvent$ calls
            const postLotsObservables = extractSCORE.dossier.map((dossier: fselib.Dossier) => {
              try {
                if (!Array.isArray(dossier.PJ)) {
                  const f = dossier.PJ;
                  dossier.PJ = [];
                  dossier.PJ.push(f); // Wrap dossier in an array if it's not already one
                }
                dossier.numIdentFact = numIdentFact;
                //console.log('Processing dossier:', dossier);

                return this.postDocEvent$(environment.server_url, dossier).pipe(
                  // Return the observable for the postDocEvent$ call
                  catchError((error: any) => {
                    console.error('Error posting dossier:', error);
                    return of({ success: false, msg: String(error?.message || error) });
                  }),
                );
              } catch (e) {
                console.error('Error in processing dossier:', e);
                return of({ success: false, msg: String(e) });
              }
            });

            if (postLotsObservables.length === 0) {
              return of(extractSCORE);
            }

            return forkJoin(postLotsObservables).pipe(
              // Wait for all postDocEvent$ calls to complete, then update the last extraction date
              switchMap((responses: any[]) => {
                (extractSCORE as any).responses = responses;
                return this.putLastExtractionDate$('docs').pipe(
                  // Update the last extraction date with generation date -10 s
                  map(() => extractSCORE), // Return the original extractSCORE with attached responses
                  catchError((error: any) => {
                    console.error('[updateFses] Error updating last extraction date:', error);
                    return throwError(() => new Error('Error updating last extraction date'));
                  }),
                );
              }),
              catchError((error: any) => {
                console.error('[updateFses] Error during postDocEvent$ calls:', error);
                return throwError(() => new Error('Error during postDocEvent$ calls'));
              }),
            );
          }),
          catchError((error: any) => {
            console.error('[updateFses] An error occurred during the extraireSCOR process:', error);
            return throwError(() => new Error('Erreur au niveau de extraireSCOR'));
          }),
        );
      }),
    );
  }
}

export type FacturePreteLot = {
  numFacture: string;
  dateFacture: string;
};

export interface Virement {
  // Virements processed structure from API
  numEmetFic: string; // num_emet_fic
  dateComptable: string; // date_comptable (ISO string)
  libVirement: string; // libVirement1 || libVirement1
  libVirementLines: string[];
  montant: number;
  rsps: fselib.FseRsp[];

  isPonctuel: boolean; // Derived. At least 1 RSP contains a "paiement ponctuel"
}

// Utility class to speed up facture lookup
export class FactureSearcher {
  private factureMap: Map<string, FacturePreteLot>;

  constructor(private factures: FacturePreteLot[]) {
    this.factureMap = new Map<string, FacturePreteLot>();
    if (factures) {
      this.buildFactureMap();
    }
  }

  // Build a map for quick searching
  private buildFactureMap(): void {
    this.factures.forEach(facture => {
      this.factureMap.set(facture.numFacture, facture);
    });
  }

  // Search for a facture by numFacture and remove it from the map if found
  public findFacture(numFacture: string): FacturePreteLot | undefined {
    const facture = this.factureMap.get(numFacture);

    if (facture) {
      this.factureMap.delete(numFacture); // Remove the entry from the map
    }
    return facture;
  }

  // Return an array of JSON objects based on the current factureMap
  public getFacturesAsJson(): FacturePreteLot[] {
    return Array.from(this.factureMap.values());
  }

  public getMaplength() {
    return this.factureMap.size;
  }
}

// Utility class
export class DateHelper {
  static convertDateToFseLocalFormat(date: Date): string {
    const pad = (n: number): string => (n < 10 ? '0' : '') + n;

    // Using local time methods
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  }
}

@Injectable({ providedIn: 'root' })
export class StateHelper {
  getEtatFactureClass(state: string | undefined): string {
    // Get etatFactureRSPClass from etatFactureRSP. Quite trivial. Just for performance (pre-calculation in init)
    switch (state) {
      case 'N':
        return 'etat-new';
      case 'S':
        return 'etat-secured';
      case 'P':
        return 'etat-paye';
      case 'PF':
        return 'etat-paye';
      case 'R':
        return 'etat-rejete';
      case 'ERREUR':
        return 'etat-rejete'; // treat ERREUR like rejected
      case 'D':
        return 'etat-differe';
      case 'A':
        return 'etat-anomalie';
      case 'C':
        return 'etat-encours';
      default:
        return '';
    }
  }

  getEtatFacture(fo: fselib.FSEOrder | undefined, state: fselib.EtatFactureRSP | string | undefined, long: boolean = false): string {
    // Compute FSEOrder.etat_facture ONLY for this page displaying. Change only RSP (final extraire_lots state) to reflect changes after RSPs integration
    if (fo && fo.etat_facture !== 'RSP' && fo.etat_facture !== 'ARL_POSITIF') return fo.etat_facture;
    if (!state) return 'INCONNU';
    const labels = long ? this.statusLabelLong : this.statusLabelShort;
    if (!state || !(state in labels)) return 'INCONNU';
    return labels[state as fselib.EtatFactureRSP];
  }

  // RSP can be 'ANOMALIE' while traitement == Payé => process this case
  getEtatRsp(rsp: fselib.RSP, partAmo: boolean): { label: string; class: string } {
    let ret;
    const part = partAmo ? rsp.partAMO : rsp.partAMC;
    const traitement = part?.traitement as fselib.EtatFactureRSP;

    if (traitement !== 'R' && traitement !== 'D' && part?.montantDemande !== part?.montantPaiement) {
      // Cas Anomalie (cas particulier D est prioritaire sur A)
      ret = { label: this.statusLabelShort['A'], class: this.getEtatFactureClass('A') };
    } else {
      ret = traitement ? { label: this.statusLabelShort[traitement], class: this.getEtatFactureClass(traitement) } : { label: 'INCONNU', class: '' };
    }
    // console.log('ret: ', JSON.stringify(ret, null, 2));
    return ret;
  }

  statusLabelLong: Record<fselib.EtatFactureRSP, string> = {
    N: 'Nouvelle Facture',
    S: 'Facture sécurisée',
    P: 'Facture payée',
    PF: 'Facture payée (forçage)',
    R: 'Facture rejetée',
    D: 'Facture différée',
    C: 'En cours',
    NO: 'Non applicable',
    A: 'Anomalie',
  };

  statusLabelShort: Record<fselib.EtatFactureRSP, string> = {
    N: 'NOUVELLE',
    S: 'SECURISÉE',
    P: 'PAYÉE',
    PF: 'PAYÉE (forçage)',
    R: 'REJETÉE',
    D: 'DIFFÉRÉE',
    C: 'EN COURS',
    NO: 'NA',
    A: 'ANOMALIE',
  };

  natureDetteLabels: Record<string, string> = {
    ACC: 'Acompte Sur Prestations',
    AFF: 'Affranchissement',
    API: 'Acompte Caisse Centralisatrice Des Paiements',
    APR: 'Avance Sur Prestations',
    IEC: 'Indu Suite A Erreur Comptable',
    IND: 'Indu Sur Prestations (Erreur Tech. De Dest)',
    IPH: 'Indu Sur Avance Pharmacie',
    IPI: 'Creance Non Geree Par Caisse Gestionnaire (En Dispositif Caisse Centralisatrice Des Paiements)',
    ISP: 'Impaye Mandat Ou Virement Solde',
    ODL: 'Opposition Interne Paiement En Double',
    OID: 'Opposition Interne Diverse',
    REL: 'Reliquat',
    VMI: 'Virement Ou Mandat Impaye',
    PSQ: 'Indu',
  };

  natureOperationLabels: Record<string, string> = {
    ANT: 'Annulation Totale',
    CIN: 'Constat Initial',
    LOP: 'Main Levee D’Opposition Partielle',
    LOT: 'Main Levee D’Opposition Totale',
    NVP: 'Admission En Non Valeur Partielle',
    NVT: 'Admission En Non Valeur Totale',
    PAI: 'Paiement',
    PPR: 'Passage En Profit',
    RAS: 'Remboursement A L’Assure',
    RDP: 'Remise De Dette Partielle',
    RDS: 'Remise De Dette < Seuil',
    RDT: 'Remise De Dette Totale',
    REL: 'Rectif. D’Un Element Signaletique',
    RMO: 'Rectification D’Un Montant',
    RPR: 'Retenue Sur Prestation',
  };

  typeRetourLabels: Record<string, string> = {
    RP: 'Paiement',
    RJ: 'Rejet',
    '01': 'Retours Sur FSE',
    '02': 'Retours Sur Prestations Papier',
    '03': 'Paiements Ponctuels Ou Forfaitaires',
    '04': 'Rappels Sur Prestations',
    '05': 'Retenues',
  };

  risque: Record<string, string> = {
    AS: 'Maladie',
    AT: 'Accident du travail',
    MA: 'Maternité',
  };

  traitementPartAMC: Record<string, string> = {
    N: "Envoi De L'Image Décompte En Noemie 1 Suite à Tp Sur La Part Amc",
    V: 'Virement Distincts Amo / Amc',
    C: 'Paiement Coordonné',
    E: 'Eclatement Sur Le Poste',
    A: 'Eclatement Par Un OCT',
  };
}
